<template>
  <div>
    <FixedNuxtErrorBoundary
      :capture-exception="captureException"
      :toast-error="(m: string) => toast.error(m)"
      :debug="boundaryDebug"
      :enabled="boundaryEnabled"
      :schedule-after-ready="scheduleAfterReady"
      :on-handled="onBoundaryHandled"
      @error="(error) => emit('error', error)"
    >
      <template #error="{ error }">
        <slot
          name="error"
          :error="error"
        >
          <p>
            An unexpected error occurred
            <pre v-if="config.public.env !== 'prod'">{{ error }}</pre>
          </p>
        </slot>
      </template>
      <Suspense
        :timeout="timeout"
        :suspensible="suspensible"
      >
        <slot />
        <template #fallback>
          <slot name="loader">
            <Loader />
          </slot>
        </template>
      </Suspense>
    </FixedNuxtErrorBoundary>
  </div>
</template>

<script setup lang="ts">
import { onNuxtReady, useNuxtApp, useRuntimeConfig } from "#imports"
import { FixedNuxtErrorBoundary } from "@effect-app/vue-components"
import { captureException } from "@sentry/nuxt"
import type { ComponentPublicInstance } from "vue"

const emit = defineEmits<{
  (e: "error", error: Error): void
}>()

const config = useRuntimeConfig()
const nuxtApp = useNuxtApp()
const toast = useToast()
const boundaryDebug = config.public.env === "local-dev"
// `import.meta.client` is NOT valid inside a Vue template expression (Vue's
// parser rejects `import.meta`), so compute it in <script setup> and bind the const.
const boundaryEnabled = import.meta.client
const scheduleAfterReady = (fn: () => void) => nuxtApp.isHydrating ? onNuxtReady(fn) : fn()
const onBoundaryHandled = (err: Error, instance: unknown, info: string) =>
  nuxtApp.hooks.callHook("vue:error", err, instance as ComponentPublicInstance | null, info)

type BaseProps = {
  timeout?: number
  suspensible?: boolean
}
withDefaults(defineProps<BaseProps>(), {
  timeout: 500,
  guard: undefined
})
</script>

<script lang="ts">
/** Internal use, use ErrorBoundary instead */
export default {
  name: "Suspender"
}
</script>
